import { canResumeCoverImageConflict } from "./coverImageConflict";
import { normalizeAccountStoryDocument, type AccountStoryDocument, type ProductDocumentKind } from "@private-fiction/story-core";
import { getSupabaseClient } from "./supabase";
import { compactStoryPayload } from "./compactStoryPayload";
import { canResumeStreamConflict } from "./streamConflict";
import { isRevisionConflict, isSyncBlocked } from "./syncFailure";
import {
  acceptAccountMutation, rebaseAccountMutation, noteAccountMutationFailure, getAccountCursor, listPendingAccountMutations, loadAccountDocuments,
  mergeRemoteAccountDocument, saveLocalAccountDocument, setAccountCursor,
  type LocalAccountDocument, type PendingAccountMutation,
} from "./accountStore";

export type AccountSyncPhase = "local" | "syncing" | "synced" | "offline" | "conflict" | "error";
export interface AccountSyncState { phase: AccountSyncPhase; pending: number; message?: string }
export interface AccountLibrary { bots: AccountStoryDocument[]; sessions: AccountStoryDocument[]; settings: Record<string, unknown> | null }

interface RemoteAccountDocument {
  kind: ProductDocumentKind;
  localId: string;
  revision: number;
  payload: Record<string, unknown>;
  deletedAt: string | null;
  changeSeq: number;
  updatedAt: string;
}

interface MutationResult {
  mutationId: string;
  status: "accepted" | "conflict";
  revision: number;
  changeSeq: number;
  payload?: Record<string, unknown> | null;
}

// A session document can contain a long transcript and media manifest. Pulling
// 500 of those into one jsonb_agg can exceed the hosted Postgres statement
// timeout even when an account owns only a few large routes.
const ACCOUNT_PULL_PAGE_SIZE = 20;
const ACCOUNT_PULL_MAX_PAGES = 250;

function projectLibrary(records: LocalAccountDocument[]): AccountLibrary {
  const bots: AccountStoryDocument[] = [];
  const sessions: AccountStoryDocument[] = [];
  let settings: Record<string, unknown> | null = null;
  for (const record of records) {
    if (record.kind === "settings") { settings = record.payload; continue; }
    const document = normalizeAccountStoryDocument(record.payload);
    if (!document) continue;
    (record.kind === "bot" ? bots : sessions).push(document);
  }
  bots.sort((left, right) => right.lastOpenedAt - left.lastOpenedAt);
  sessions.sort((left, right) => right.lastOpenedAt - left.lastOpenedAt);
  return { bots, sessions, settings };
}

const remoteMutation = (mutation: PendingAccountMutation) => ({
  mutationId: mutation.id, kind: mutation.kind, localId: mutation.localId,
  operation: mutation.operation, expectedRevision: mutation.expectedRevision, payload: compactStoryPayload(mutation.payload),
});

export class AccountRepository {
  private syncing = new Map<string, Promise<{ library: AccountLibrary; state: AccountSyncState }>>();
  async load(scope: string): Promise<AccountLibrary> {
    return projectLibrary(await loadAccountDocuments(scope));
  }

  async upsert(scope: string, kind: "bot" | "session", document: AccountStoryDocument): Promise<AccountLibrary> {
    await saveLocalAccountDocument(scope, kind, document.id, document);
    return this.load(scope);
  }

  async upsertSettings(scope: string, settings: Record<string, unknown>): Promise<AccountLibrary> {
    await saveLocalAccountDocument(scope, "settings", "account", settings);
    return this.load(scope);
  }

  async sync(scope: string): Promise<{ library: AccountLibrary; state: AccountSyncState }> {
    const current = this.syncing.get(scope);
    if (current) return current;
    const work = Promise.resolve().then(() => navigator.locks
      ? navigator.locks.request(`mvp-story-account-sync:${scope}`, () => this.performSync(scope))
      : this.performSync(scope));
    this.syncing.set(scope, work);
    try { return await work; } finally { this.syncing.delete(scope); }
  }

  private async performSync(scope: string, streamRecoveryBudget = 1): Promise<{ library: AccountLibrary; state: AccountSyncState }> {
    const supabase = getSupabaseClient();
    let pending = await listPendingAccountMutations(scope);
    const blocked = pending.find(isSyncBlocked);
    if (blocked) return { library: await this.load(scope), state: { phase: isRevisionConflict(blocked) ? "conflict" : "error", pending: pending.length, message: "พักรายการซิงก์ที่ล้มเหลวไว้เพื่อตรวจสอบ ข้อมูลในเครื่องยังอยู่" } };
    if (pending.some(item => (item.nextAttemptAt ?? 0) > Date.now())) return { library: await this.load(scope), state: { phase: "local", pending: pending.length, message: "รอก่อนลองเชื่อมต่อ Cloud อีกครั้ง" } };
    if (!supabase) return { library: await this.load(scope), state: { phase: "local", pending: pending.length } };
    const { data: auth } = await supabase.auth.getSession();
    if (!auth.session) return { library: await this.load(scope), state: { phase: "local", pending: pending.length, message: "เข้าสู่ระบบเพื่อเชื่อมข้อมูลของบัญชี" } };
    if (!navigator.onLine) return { library: await this.load(scope), state: { phase: "offline", pending: pending.length } };

    for (const mutation of pending) {
      const { data, error } = await supabase.rpc("apply_account_mutations_v3", { p_mutations: [remoteMutation(mutation)] })
        .then(response => response, (error: unknown) => ({ data: null, error: { message: error instanceof Error ? error.message : String(error) } }));
      if (error) {
        await noteAccountMutationFailure(mutation, error);
        return { library: await this.load(scope), state: { phase: "error", pending: (await listPendingAccountMutations(scope)).length, message: error.message } };
      }
      const result = (data?.results?.[0] ?? null) as MutationResult | null;
      if (!result) {
        await noteAccountMutationFailure(mutation, new Error("ระบบคลาวด์ไม่สามารถบันทึกข้อมูลบัญชีได้"));
        return { library: await this.load(scope), state: { phase: "error", pending: (await listPendingAccountMutations(scope)).length, message: "ระบบคลาวด์ไม่สามารถบันทึกข้อมูลบัญชีได้" } };
      }
      if (result.status === "accepted") {
        await acceptAccountMutation(scope, mutation, Number(result.revision), Number(result.changeSeq));
      } else {
        if (streamRecoveryBudget > 0 && mutation.kind === "bot" && result.payload && canResumeCoverImageConflict(compactStoryPayload(mutation.payload), result.payload)) {
          await rebaseAccountMutation(mutation, Number(result.revision));
          return this.performSync(scope, streamRecoveryBudget - 1);
        }
        if (streamRecoveryBudget > 0 && mutation.kind === "session" && result.payload && canResumeStreamConflict(compactStoryPayload(mutation.payload), result.payload)) {
          await rebaseAccountMutation(mutation, Number(result.revision));
          return this.performSync(scope, streamRecoveryBudget - 1);
        }
        // Preserve authoritative sync semantics: never overwrite a newer cloud
        // revision automatically. Keep the exact local mutation for explicit
        // recovery instead of silently rebasing and retrying stale content.
        await noteAccountMutationFailure(mutation, { code: "PT409", message: "revision conflict for account document" });
        return {
          library: await this.load(scope),
          state: {
            phase: "conflict",
            pending: (await listPendingAccountMutations(scope)).length,
            message: "ข้อมูลเดียวกันถูกแก้จากอีกอุปกรณ์ ระบบเก็บฉบับปัจจุบันไว้และยังไม่เขียนทับ Cloud",
          },
        };
      }
    }

    let cursor = await getAccountCursor(scope);
    let pageSize = ACCOUNT_PULL_PAGE_SIZE;
    for (let page = 0; page < ACCOUNT_PULL_MAX_PAGES; page += 1) {
      const { data, error } = await supabase.rpc("pull_account_documents_v3", { p_after: cursor, p_limit: pageSize });
      if (error && (error.code === "57014" || /statement timeout/i.test(error.message)) && pageSize > 1) { pageSize = Math.max(1, Math.floor(pageSize / 2)); continue; }
      if (error) return { library: await this.load(scope), state: { phase: "error", pending: 0, message: error.message } };
      const rows = (data?.documents ?? []) as RemoteAccountDocument[];
      for (const row of rows) await mergeRemoteAccountDocument(scope, {
        kind: row.kind, localId: row.localId, revision: Number(row.revision), payload: row.payload ?? {},
        deletedAt: row.deletedAt, changeSeq: Number(row.changeSeq), updatedAt: row.updatedAt,
      });
      const nextCursor = Number(data?.cursor ?? cursor);
      if (rows.length && (!Number.isFinite(nextCursor) || nextCursor <= cursor)) return { library: await this.load(scope), state: { phase: "error", pending: 0, message: "Cloud cursor ไม่เดินหน้า จึงหยุดอ่านซ้ำ" } };
      if (nextCursor > cursor) { cursor = nextCursor; await setAccountCursor(scope, cursor); }
      if (rows.length < pageSize) break;
    }
    pending = await listPendingAccountMutations(scope);
    return { library: await this.load(scope), state: { phase: "synced", pending: pending.length } };
  }
}
